import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useContactLists } from '@/hooks/useContactLists';
import { Plus, List } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * ListModal - Create a new list or add to existing list
 *
 * Props:
 *   open: boolean
 *   onOpenChange: (open: boolean) => void
 *   contactIds: string[]   (ids to add)
 *   dealIds: string[]      (ids to add)
 *   onSuccess?: () => void
 */
export default function ListModal({ open, onOpenChange, contactIds = [], dealIds = [], onSuccess }) {
  const [mode, setMode] = useState('existing'); // 'existing' | 'create'
  const [selectedListId, setSelectedListId] = useState('');
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');

  const { lists, isLoading, createList, isCreating, addToList, isAdding } = useContactLists();

  const isPending = isCreating || isAdding;
  const totalItems = contactIds.length + dealIds.length;

  const handleClose = () => {
    onOpenChange(false);
    setMode('existing');
    setSelectedListId('');
    setNewName('');
    setNewDescription('');
  };

  const handleSubmit = async () => {
    try {
      if (mode === 'create') {
        if (!newName.trim()) return;
        await createList({ name: newName.trim(), description: newDescription.trim() || null, contactIds, dealIds });
      } else {
        if (!selectedListId) return;
        await addToList({ listId: selectedListId, contactIds, dealIds });
      }
      onSuccess?.();
      handleClose();
    } catch {
      // toast handled inside hook
    }
  };

  const canSubmit = mode === 'create' ? newName.trim().length > 0 : selectedListId !== '';

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            Adicionar {totalItems} {totalItems === 1 ? 'item' : 'itens'} a uma lista
          </DialogTitle>
        </DialogHeader>

        {/* Mode Tabs */}
        <div className="flex border border-border rounded-lg overflow-hidden">
          <button
            className={cn(
              'flex-1 py-2 text-sm font-medium transition-colors',
              mode === 'existing'
                ? 'bg-primary text-primary-foreground'
                : 'bg-background text-muted-foreground hover:bg-muted'
            )}
            onClick={() => setMode('existing')}
          >
            Lista existente
          </button>
          <button
            className={cn(
              'flex-1 py-2 text-sm font-medium transition-colors border-l border-border',
              mode === 'create'
                ? 'bg-primary text-primary-foreground'
                : 'bg-background text-muted-foreground hover:bg-muted'
            )}
            onClick={() => setMode('create')}
          >
            Nova lista
          </button>
        </div>

        {mode === 'existing' ? (
          <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
            {isLoading ? (
              <p className="text-sm text-muted-foreground text-center py-4">Carregando listas...</p>
            ) : lists.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                Nenhuma lista criada ainda.
              </p>
            ) : (
              lists.map((list) => (
                <button
                  key={list.id}
                  onClick={() => setSelectedListId(list.id)}
                  className={cn(
                    'w-full text-left rounded-lg border p-3 transition-colors',
                    selectedListId === list.id
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50 hover:bg-muted/50'
                  )}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm">{list.name}</span>
                    <span className="text-xs text-muted-foreground">{list.item_count} itens</span>
                  </div>
                  {list.description && (
                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{list.description}</p>
                  )}
                </button>
              ))
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="list-name">Nome da lista *</Label>
              <Input
                id="list-name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Ex: Leads para campanha maio"
                autoFocus
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="list-description">Descrição (opcional)</Label>
              <Textarea
                id="list-description"
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                placeholder="Descreva o objetivo desta lista..."
                rows={2}
              />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isPending}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit || isPending}>
            {isPending ? 'Salvando...' : mode === 'create' ? 'Criar e adicionar' : 'Adicionar à lista'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
